import { useTranslation } from 'react-i18next';
import React, { useRef } from 'react';
import _ from 'lodash';
import { APIProvider } from '@vis.gl/react-google-maps';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { NewAddressModalBody } from '../../components/location/new-address-modal-body';
import { SelectAddressModalTitle } from '../../components/location/select-address-modal-title';
import { layoutDefaults } from '../../utils/layout-defaults';
import { useAnalytics } from '../../utils/setup/analytics-setup';
import { usePublicAPI } from '../../apis/public-api';
import { useMapAPI } from '../../apis/map-api';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { NewAddressModalTitle } from '../../components/location/new-address-modal-title';

function useNewAddressModal() {
  const storage = useStorage();
  const { config, menuInfo } = storage;
  const analytics = useAnalytics();
  const { analyticsPageView } = analytics;
  const { swal } = useSwal({ storage });
  const { getAddressPrivacyPolicyFromRemote } = usePublicAPI();
  const { saveMapBaseLocationFromRemote } = useMapAPI();
  const { handleErrorOnModal } = useErrorHandler();
  // const googleMapsAPI = useGoogleMapsAPI();
  const { t } = useTranslation();

  const defaultFilterRef = useRef();
  const defaultDataRef = useRef([]);

  const didOpen = () => {
    analyticsPageView('New Address');
  };

  async function clear() {
    defaultFilterRef.current = null;
    defaultDataRef.current = [];
  }

  const onMessage = (message) => {
    if (_.isEmpty(message)) {
      swal.resetValidationMessage();
    } else {
      swal.showValidationMessage(message);
    }
  };

  // const onError = (error) => {
  //   // TODO tratar os erros
  //   console.log('error', error);
  // };

  async function show({
    showBackButton,
    avoidAnimation,
    wasBackButtonClicked,
  }) {
    let placeDetails = null;
    let baseLocation = null;
    let wasCurrentLocationButtonClicked = false;

    let filter = null;
    let data = null;

    const result = await swal.fire({
      title: showBackButton ? (
        <NewAddressModalTitle
          style={{
            marginTop: layoutDefaults.biggerVerticalMargin,
          }}
          title={t('new-address-modal.title')}
        />
      ) : (
        <SelectAddressModalTitle
          style={{
            marginTop: layoutDefaults.biggerVerticalMargin,
          }}
          title={t('select-address-modal.title')}
          analytics={analytics}
          getPrivacyPolicyFromRemote={getAddressPrivacyPolicyFromRemote}
        />
      ),
      html: (
        <APIProvider
          apiKey={config?.maps?.browser?.api_key}
          language={menuInfo?.i18n?.default_language?.tag}
        >
          <NewAddressModalBody
            defaultFilter={defaultFilterRef.current}
            defaultData={defaultDataRef.current}
            storage={storage}
            onFilterChange={(pFilter) => {
              filter = pFilter;
            }}
            onDataChange={(pData) => {
              data = pData;
            }}
            onSelect={async (pPlaceDetails, setLoading) => {
              try {
                const response =
                  await saveMapBaseLocationFromRemote(pPlaceDetails);

                baseLocation = response.data;
                placeDetails = pPlaceDetails;

                defaultFilterRef.current = filter;
                defaultDataRef.current = data;

                setLoading(false);
                swal.clickConfirm();
              } catch (error) {
                handleErrorOnModal(error);
              }
            }}
            onCurrentLocationClick={async (setLoading) => {
              _.delay(() => {
                wasCurrentLocationButtonClicked = true;
                swal.clickConfirm();
                setLoading(false);
              }, layoutDefaults.keyboardDuration);
            }}
            // onError={onError}
            onMessage={onMessage}
          />
        </APIProvider>
      ),
      didOpen,
      showConfirmButton: false,
      showDenyButton: false,
      showCancelButton: true,
      confirmButtonText: t('save'),
      cancelButtonText: showBackButton ? t('back') : t('cancel'),
      showClass:
        wasBackButtonClicked || showBackButton || avoidAnimation
          ? null
          : {
              popup: 'animate__animated animate__slideInLeft animate__faster',
            },
    });

    result.placeDetails = placeDetails;
    result.baseLocation = baseLocation;
    result.wasCurrentLocationButtonClicked = wasCurrentLocationButtonClicked;
    result.wasNewAddressBackButtonClicked =
      result.isDismissed && showBackButton;

    return result;
  }

  return {
    clearNewAddressModal: clear,
    showNewAddressModal: show,
  };
}

export { useNewAddressModal };
